import React, { useEffect, useState, useCallback } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { host } from '../script/variables'
import '../style/usermedia.css'

function timeAgo(ts) {
  const diff = Date.now() - new Date(ts)
  const s = Math.floor(diff / 1000), m = Math.floor(s / 60), h = Math.floor(m / 60)
  const d = Math.floor(h / 24), w = Math.floor(d / 7), mo = Math.floor(d / 30)
  if (mo > 0) return `${mo}mo ago`
  if (w  > 0) return `${w}w ago`
  if (d  > 0) return `${d}d ago`
  if (h  > 0) return `${h}h ago`
  if (m  > 0) return `${m}m ago`
  return s <= 5 ? 'just now' : `${s}s ago`
}

const Media = ({ id }) => {
  const [media, setMedia]       = useState([])
  const [pageNo, setPageNo]     = useState(1)
  const [hasMore, setHasMore]   = useState(true)
  const [loading, setLoading]   = useState(false)

  /* lightbox state */
  const [lbOpen, setLbOpen]     = useState(false)
  const [lbIndex, setLbIndex]   = useState(0)

  const fetchUserMedia = useCallback(async (pno) => {
    if (loading) return
    setLoading(true)
    try {
      const res = await fetch(`${host}/masala/usermedia?id=${id}&pno=${pno}`, {
        headers: { 'Content-Type': 'application/json' },
      })
      if (res.ok) {
        const data = await res.json()
        const items = data.userMediaWithCafeDetails || []
        setMedia(prev => pno === 1 ? items : [...prev, ...items])
        if (items.length < 10) setHasMore(false)
        setPageNo(pno + 1)
      }
    } catch {}
    finally { setLoading(false) }
  }, [id, loading])

  useEffect(() => {
    if (id) { setPageNo(1); setMedia([]); fetchUserMedia(1) }
  }, [id])

  /* keyboard nav for lightbox */
  useEffect(() => {
    if (!lbOpen) return
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setLbIndex(i => (i + 1) % media.length)
      if (e.key === 'ArrowLeft')  setLbIndex(i => (i - 1 + media.length) % media.length)
      if (e.key === 'Escape')     setLbOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lbOpen, media.length])

  const openLb  = (idx) => { setLbIndex(idx); setLbOpen(true) }
  const closeLb = ()    => setLbOpen(false)
  const prevImg = (e)   => { e.stopPropagation(); setLbIndex(i => (i - 1 + media.length) % media.length) }
  const nextImg = (e)   => { e.stopPropagation(); setLbIndex(i => (i + 1) % media.length) }

  const cur = media[lbIndex]

  return (
    <div className="media-root">

      {/* ── Lightbox ── */}
      {lbOpen && cur && (
        <div className="media-lb-backdrop" onClick={closeLb}>
          <div className="media-lb-box" onClick={e => e.stopPropagation()}>

            {/* image area */}
            <div className="media-lb-img-wrap">
              <img className="media-lb-img" src={cur.imageurl} alt={cur.caption || ''} />
              {media.length > 1 && <>
                <button className="media-lb-arrow prev" onClick={prevImg}>‹</button>
                <button className="media-lb-arrow next" onClick={nextImg}>›</button>
              </>}
              <button className="media-lb-close" onClick={closeLb}>×</button>
            </div>

            {/* dots */}
            {media.length > 1 && (
              <div className="media-lb-dots">
                {media.slice(0, Math.min(media.length, 12)).map((_, i) => (
                  <div
                    key={i}
                    className={`media-lb-dot${i === lbIndex ? ' active' : ''}`}
                    onClick={() => setLbIndex(i)}
                  />
                ))}
              </div>
            )}

            {/* info */}
            <div className="media-lb-info">
              <div className="media-lb-meta">
                {cur.cafeDetails && (
                  <a
                    className="media-lb-cafe"
                    href={`${host}/moredetails?cafeid=${cur.cafeDetails._id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    📍 {cur.cafeDetails.name}
                  </a>
                )}
                <span className="media-lb-time">{timeAgo(cur.createdAt)}</span>
              </div>
              {cur.caption && <p className="media-lb-caption">{cur.caption}</p>}
            </div>
          </div>
        </div>
      )}

      {/* ── Grid ── */}
      {media.length === 0 && !loading ? (
        <div className="media-empty">
          <div className="media-empty-icon">🖼️</div>
          <p>No photos yet</p>
        </div>
      ) : (
        <div id="media-scroll-target" style={{ height: 480, overflow: 'auto' }}>
          <InfiniteScroll
            dataLength={media.length}
            next={() => fetchUserMedia(pageNo)}
            hasMore={hasMore}
            scrollableTarget="media-scroll-target"
            loader={<div className="media-loader"><div className="media-spinner" /></div>}
            endMessage={<p className="media-end">✦ All photos loaded ✦</p>}
          >
            <div className="media-grid">
              {media.map((item, idx) => (
                <div
                  key={item._id || idx}
                  className="media-thumb"
                  onClick={() => openLb(idx)}
                  style={{ animationDelay: `${Math.min(idx, 12) * 30}ms` }}
                >
                  <img src={item.imageurl} alt={item.caption || 'photo'} loading="lazy" />
                  <div className="media-thumb-overlay">
                    {item.caption && <span>{item.caption}</span>}
                  </div>
                </div>
              ))}
            </div>
          </InfiniteScroll>
        </div>
      )}
    </div>
  )
}

export default Media